/**
 * @module UseContainerTriage
 * @purpose State machine for the artefact-triage panel: loads grouped
 *   inventory, tracks group/item selection with a gated re-ingest
 *   confirmation, and submits a selective ingestion run (pattern:
 *   `use-upload-receipt-modal.ts`).
 * @depends use-container-triage-inventory, use-container-triage-selection,
 *   use-container-triage-submit
 * @dependedOnBy ContainerTriagePanel
 *
 * @rules
 * - State: composes three focused sub-hooks (inventory/selection/submit)
 *   rather than one large hook, to stay under the function-length budget
 * - Performance: `kindFilter` is forwarded to useContainerTriageInventory,
 *   which applies it server-side (`?kind=…`) -- `visibleGroups` is the
 *   inventory's groups as returned, no client-side re-filtering
 * - Errors: `error` prefers a submit failure over a load failure since it is
 *   the more recent user-facing event; `retry` resolves whichever is set
 */

import { useCallback, useState } from 'react'
import type { ContainerTriagePhase, ContainerTriageState } from './container-triage.types'
import { useContainerTriageInventory } from './use-container-triage-inventory'
import { useContainerTriageSelection } from './use-container-triage-selection'
import { useContainerTriageSubmit } from './use-container-triage-submit'
import type { InventoryItemKind } from '../../../types/evidence-containers'

function derivePhase(args: {
  inventoryLoading: boolean
  inventoryError: string | null
  pendingReingest: boolean
  submitting: boolean
  submitted: boolean
  submitError: string | null
}): ContainerTriagePhase {
  const { inventoryLoading, inventoryError, pendingReingest, submitting, submitted, submitError } = args
  if (inventoryLoading) return 'loading'
  if (submitting) return 'submitting'
  if (submitted) return 'submitted'
  if (submitError !== null) return 'error'
  if (inventoryError !== null) return 'error'
  if (pendingReingest) return 'confirmingReingest'
  return 'ready'
}

export function useContainerTriage(caseId: string, receiptId: string): ContainerTriageState {
  const [kindFilter, setKindFilter] = useState<InventoryItemKind | null>(null)
  const inv = useContainerTriageInventory(caseId, receiptId, kindFilter)
  const selection = useContainerTriageSelection()
  const submitState = useContainerTriageSubmit()

  const phase = derivePhase({
    inventoryLoading: inv.loading,
    inventoryError: inv.error,
    pendingReingest: selection.pendingReingestItem !== null,
    submitting: submitState.submitting,
    submitted: submitState.submittedRun !== null,
    submitError: submitState.error,
  })

  const visibleGroups = inv.inventory?.groups ?? []

  const submit = useCallback(async () => {
    await submitState.submit({
      caseId,
      receiptId,
      selectedKeys: selection.selectedKeys,
      reingestKeys: selection.reingestKeys,
    })
  }, [caseId, receiptId, selection.selectedKeys, selection.reingestKeys, submitState])

  const retry = useCallback(() => {
    if (submitState.error !== null) {
      submitState.reset()
      return
    }
    void inv.refetch()
  }, [submitState, inv])

  return {
    phase,
    inventory: inv.inventory,
    visibleGroups,
    kindFilter,
    setKindFilter,
    selectedKeys: selection.selectedKeys,
    reingestKeys: selection.reingestKeys,
    pendingReingestItem: selection.pendingReingestItem,
    toggleItem: selection.toggleItem,
    toggleGroup: selection.toggleGroup,
    confirmReingest: selection.confirmReingest,
    cancelReingest: selection.cancelReingest,
    inventoryOperationId: inv.inventoryOperationId,
    triggerInventory: inv.triggerInventory,
    handleInventoryOperationComplete: inv.handleInventoryOperationComplete,
    submittedRun: submitState.submittedRun,
    submittedOperationId: submitState.submittedOperationId,
    handleRunOperationComplete: submitState.handleRunOperationComplete,
    error: submitState.error ?? inv.error,
    submit,
    retry,
  }
}
